import {serializable} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';

export type PropPathPart = string | number;
export type PropPathReducerFn<T> = (current:T, partAsString:string, partAsNumber:number) => T;

export class PropPath
{
    public static readonly ROOT:PropPath = new PropPath([]);

    @serializable
    public readonly parts:Array<PropPathPart>;

    private _key:string;

    constructor(parts:Array<PropPathPart>)
    {
        this.parts = parts;
    }

    public plus(part:PropPathPart):PropPath
    {
        return new PropPath([...this.parts, part]);
    }

    public slice(begin:number, end?:number):PropPath
    {
        return new PropPath(this.parts.slice(begin, end));
    }

    public pop():PropPath
    {
        return this.slice(0, this.length() - 1);
    }

    public shift():PropPath
    {
        return this.slice(1);
    }

    public length():number
    {
        return this.parts.length;
    }

    public isRoot():boolean
    {
        return this.parts.length === 0;
    }

    public withPartAt(index:number, newPart:PropPathPart):PropPath
    {
        const newParts = this.parts.slice();
        newParts[index] = newPart;

        return new PropPath(newParts);
    }

    public getPartAt(index:number):PropPathPart
    {
        // let's support negative indexes
        if( index < 0 )
            index = this.length() + index;

        return this.parts[index];
    }

    public getNumberPartAt(index:number):number
    {
        const part = this.getPartAt(index) as any;
        return isNaN(part) ? NaN : parseInt(part);
    }

    public getStringPartAt(index:number):string
    {
        const part = this.getPartAt(index);
        return part.toString();
    }

    public isEqualToFP(fieldPath:string):boolean
    {
        return this.isEqual(PropPath.fromFieldPath(fieldPath));
    }

    public isEqual(other:PropPath):boolean
    {
        if( this.parts.length !== other.parts.length )
            return false;

        let index = 0;
        let length = this.parts.length;
        while( index < length )
        {
            if( this.parts[index] !== other.parts[index] )
                return false;

            index++;
        }
        return true;
    }

    // returns *true* for equal paths
    public isPrefixOf(whole:PropPath):boolean
    {
        const wholeLen = whole.length();
        const thisLen = this.length();
        if( wholeLen < thisLen )
            return false;

        for( let i = 0; i < thisLen; ++i )
        {
            if( this.parts[i] !== whole.parts[i] )
                return false;
        }

        return true;
    }

    // returns *false* for equal paths
    public isStrictPrefixOf(whole:PropPath):boolean
    {
        return whole.length() > this.length() && this.isPrefixOf(whole);
    }

    // returns *true* for equal paths
    public isSuffixOf(whole:PropPath):boolean
    {
        const wholeLen = whole.length();
        const thisLen = this.length();
        if( wholeLen < thisLen )
            return false;

        const lenDiff = wholeLen - thisLen;
        for( let i = 0; i < thisLen; ++i )
        {
            if( this.parts[i] !== whole.parts[i + lenDiff] )
                return false;
        }

        return true;
    }

    public setIn(target:any, value:any):any
    {
        // special case handling for convinience
        if( this.isRoot() )
            return value;

        let length = this.parts.length;

        let index = 0;
        let current = target;
        while( current != null && index < length - 1 )
            current = current[this.parts[index++]];

        if( current )
            //here: index === length - 1
            current[this.parts[index]] = value;

        return target;
    }

    public getIn(target:any):any
    {
        let result = target;

        let index = 0;
        let length = this.parts.length;
        while( result != null && index < length )
            result = result[this.parts[index++]];

        return index === length ? result : undefined;
    }

    public reduce<T>(initial:T, reducer:PropPathReducerFn<T>):T
    {
        let result = initial;
        for( const part of this.parts )
        {
            // only one of those should be not null
            const partAsString = isNaN(part as any) ? part as string : null;
            const partAsNumber = partAsString ? null : part as number;

            result = reducer(result, partAsString, partAsNumber);
            if( result === null )
                break;
        }

        return result;
    }

    public asKey():string
    {
        if( this._key == null )
            return this._key = this.parts.join('.');

        return this._key;
    }

    public asPropSuffix():string
    {
        const key = this.asKey();
        return key.length === 0 ? '' : `.${key}`;
    }

    public static fromString(key:string):PropPath
    {
        const parts = key.split('.').filter(part => part.length > 0);
        return new PropPath(parts);
    }

    public static fromFieldPath(path:string):PropPath
    {
        const parts = path
            .replace(/\[(\d+)\]/g, '.$1')
            .split('.')
            .filter(part => part.length > 0);

        return new PropPath(parts);
    }
}
